/**
 * Ghost cards — soft-opacity title-only echoes of premium content.
 *
 * They give the viewer a sense of investigative depth waiting beneath without
 * leaking the actual body. No blur. Just reduced opacity + a "lock dot" and
 * compact typography.
 */

import React from "react";
import { Lock } from "lucide-react";

export function GhostEvidenceCard({ item, testId }) {
    return (
        <div
            data-testid={testId || "ghost-evidence"}
            className="relative bg-[#0F1828] border border-dashed border-[#243044] rounded-xl px-5 py-4 opacity-60 hover:opacity-75 transition-opacity"
        >
            <div className="flex items-start gap-3">
                <Lock className="w-3.5 h-3.5 mt-0.5 text-[#56CCF2]/60 shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#CBD5E1] truncate">
                        {item.title || "Premium evidence"}
                    </p>
                    {(item.strength || item.position) && (
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mt-1">
                            {[item.strength, item.position].filter(Boolean).join(" · ")}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export function GhostTimelineRow({ item, testId }) {
    return (
        <div
            data-testid={testId || "ghost-timeline"}
            className="relative flex items-start gap-4 opacity-55 hover:opacity-70 transition-opacity"
        >
            <div className="text-xs font-mono text-[#64748B] min-w-[64px] pt-0.5">
                {item.date || "—"}
            </div>
            <div className="flex-1 min-w-0 flex items-start gap-2">
                <Lock className="w-3 h-3 mt-1 text-[#56CCF2]/60 shrink-0" />
                <p className="text-sm text-[#94A3B8] truncate">
                    {item.title || "Documented event"}
                </p>
            </div>
        </div>
    );
}

export function GhostSourceCard({ item, testId }) {
    return (
        <div
            data-testid={testId || "ghost-source"}
            className="relative bg-[#0F1828] border border-dashed border-[#243044] rounded-xl px-5 py-4 opacity-55 hover:opacity-70 transition-opacity"
        >
            <div className="flex items-start gap-3">
                <Lock className="w-3.5 h-3.5 mt-0.5 text-[#56CCF2]/60 shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#CBD5E1] truncate">
                        {item.title || "Cited source"}
                    </p>
                    {(item.publisher || item.source_type) && (
                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mt-1 truncate">
                            {[item.publisher, item.source_type].filter(Boolean).join(" · ")}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export function GhostEntityChip({ item }) {
    return (
        <span
            data-testid="ghost-entity"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F1828] border border-dashed border-[#243044] text-xs text-[#94A3B8] opacity-60"
        >
            <Lock className="w-2.5 h-2.5 text-[#56CCF2]/60" />
            {item.name || "Co-occurring entity"}
        </span>
    );
}

export function GhostTopicChip({ item }) {
    return (
        <span
            data-testid="ghost-topic"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F1828] border border-dashed border-[#243044] text-xs text-[#94A3B8] opacity-60"
        >
            <Lock className="w-2.5 h-2.5 text-[#56CCF2]/60" />
            {item.title || "Connected investigation"}
        </span>
    );
}
